import { DocumentType, PaymentType } from '@innedit/innedit-type';
import { GenericData } from 'packages/innedit';
import { useEffect, useState } from 'react';

// L'ESPACE EST OBLIGATOIRE, ET CE N'EST PAS UNE COMMODITÉ.
//
// Les règles Firestore ne sont pas des filtres : une requête que rien ne
// contraint à un espace ne peut être prouvée contre `resource.data.espaceId`,
// et elle est refusée. Elle ne passait jusqu'au 31/08/2026 que parce que
// `payments` portait un `allow read;` inconditionnel — celui-là même qui
// ouvrait la collection à n'importe qui, sans authentification (SPEC-469).
//
// Sans cet argument, les paiements d'une commande sont invisibles pour tout
// utilisateur qui n'est pas `type: "admin"` — et le CMS n'en compte qu'un.
const useOrderPayments = (
  orderId: string,
  espaceId: string,
): {
  paymentAmount: number;
  paymentAmountCapturable: number;
  paymentAmountReceived: number;
  paymentAmountRefunded: number;
  payments?: DocumentType<PaymentType>[];
} => {
  const [payments, setPayments] = useState<DocumentType<PaymentType>[]>();
  const [paymentAmount, setPaymentAmount] = useState<number>(0);
  const [paymentAmountCapturable, setPaymentAmountCapturable] =
    useState<number>(0);
  const [paymentAmountRefunded, setPaymentAmountRefunded] = useState<number>(0);
  const [paymentAmountReceived, setPaymentAmountReceived] = useState<number>(0);

  useEffect(() => {
    let unsub: () => void | undefined;
    if (orderId && espaceId) {
      // On recherche tous les paiements associés à ce bon de commande
      // `espaceId` passe par le constructeur, qui l'ajoute lui-même aux
      // contraintes : c'est ainsi que le fait toute autre liste du CMS.
      const genericData = new GenericData<PaymentType>({
        espaceId,
        collectionName: 'payments',
      });
      unsub = genericData.watch(
        docs => {
          let tmpPaymentAmount = 0;
          let tmpPaymentAmountCapturable = 0;
          let tmpPaymentAmountRefunded = 0;
          const tmpPaymentAmountReceived =
            docs.length > 0
              ? docs.reduce((acc, doc) => {
                  if (doc.status !== 'canceled') {
                    tmpPaymentAmount += doc.amount ?? 0;
                    tmpPaymentAmountRefunded += doc.amountRefunded ?? 0;
                    tmpPaymentAmountCapturable += doc.amountCapturable ?? 0;
                  }

                  return acc + (doc.amountReceived ?? 0);
                }, 0)
              : 0;

          setPaymentAmount(tmpPaymentAmount);
          setPaymentAmountCapturable(tmpPaymentAmountCapturable);
          setPaymentAmountRefunded(tmpPaymentAmountRefunded);
          setPaymentAmountReceived(tmpPaymentAmountReceived);

          setPayments(docs);
        },
        {
          orderDirection: 'asc',
          orderField: 'createdAt',
          wheres: {
            orderId,
          },
        },
      );
    }

    return () => {
      if (unsub) {
        unsub();
      }
    };
  }, [orderId, espaceId]);

  return {
    paymentAmount,
    paymentAmountCapturable,
    paymentAmountReceived,
    paymentAmountRefunded,
    payments,
  };
};

export default useOrderPayments;
